import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { X, Ticket } from 'lucide-react';

const GREEN = '#00FF41';
const PURPLE = '#9D00FF';
const GOLD = '#FFD700';

export default function BuzzPassport() {
  const [data, setData] = useState(null);
  const [promos, setPromos] = useState([]);
  const [fullQr, setFullQr] = useState(null); // full-screen QR modal

  useEffect(() => {
    const token = localStorage.getItem('token');
    const load = async () => {
      try {
        const res = await axios.get(`${API}/youngbuzz/passport`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setData(res.data);
      } catch (err) {
        setData({
          user_id: 'guest',
          username: 'Guest',
          qr_payload: 'nightbuzz://buzz-passport/guest',
          codes: [
            { code: 'BUZZ10', discount: '10% off', venue: 'Club Neon' },
            { code: 'FRIDAY20', discount: '20% off', venue: 'The Spot' },
          ]
        });
      }
    };
    const loadPromos = async () => {
      if (!token) { setPromos([]); return; }
      try {
        const res = await axios.get(`${API}/patron/promos/active`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPromos(res.data || []);
      } catch { setPromos([]); }
    };
    load();
    loadPromos();
    const iv = setInterval(loadPromos, 15000); // auto-refresh every 15s to catch redemptions
    return () => clearInterval(iv);
  }, []);

  const copyCode = (code) => {
    try {
      navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch { toast.info(code); }
  };

  if (!data) return null;

  return (
    <Card
      data-testid="buzz-passport"
      className="p-6 mt-5"
      style={{
        background: '#0a0a0a',
        border: `1px solid ${GREEN}66`,
        boxShadow: `0 0 25px ${GREEN}55, inset 0 0 30px ${PURPLE}22`,
      }}
    >
      <div className="text-center mb-4">
        <h2
          className="text-2xl font-black tracking-widest"
          style={{ color: GREEN, textShadow: `0 0 12px ${GREEN}, 0 0 4px ${PURPLE}`, fontFamily: 'Orbitron, sans-serif' }}
        >
          BUZZ PASSPORT
        </h2>
        <p className="text-xs mt-1" style={{ color: `${PURPLE}dd` }}>
          Digital ID · @{data.username || 'guest'}
        </p>
      </div>

      {/* Active Promos (top-priority section) */}
      {promos.length > 0 ? (
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest mb-2 font-bold flex items-center gap-2"
             style={{ color: GOLD, textShadow: `0 0 6px ${GOLD}` }}>
            <Ticket className="w-4 h-4" /> Your Active Promos
          </p>
          <div className="space-y-3">
            {promos.map(p => (
              <div key={p.id}
                data-testid={`active-promo-${p.promo_type}`}
                className="rounded-lg p-4"
                style={{
                  background: 'rgba(0,0,0,0.7)',
                  border: `1.5px solid ${GOLD}`,
                  boxShadow: `0 0 20px ${GOLD}55, inset 0 0 15px ${GREEN}22`,
                }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="text-3xl">{p.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-black text-lg" style={{ color: GOLD, textShadow: `0 0 8px ${GOLD}` }}>
                      {p.title}
                    </h3>
                    <p className="text-xs text-white/80 mt-0.5">{p.description}</p>
                    <p className="text-[10px] text-white/40 mt-1">at {p.venue_name}</p>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div style={{ background: '#fff', padding: 8, borderRadius: 6 }}>
                    <QRCodeSVG value={p.qr_data} size={120} level="M" />
                  </div>
                  <Button
                    data-testid={`show-qr-${p.promo_type}`}
                    onClick={() => setFullQr(p)}
                    className="text-black font-bold w-full"
                    style={{ background: GOLD, boxShadow: `0 0 12px ${GOLD}88` }}
                  >
                    Tap to Show Bartender
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-center text-xs text-white/60 mb-4">
          Check in to unlock your promos 🎉
        </p>
      )}

      {/* QR code */}
      <div
        className="mx-auto p-4 rounded-lg mb-5"
        style={{
          width: 'fit-content',
          background: '#fff',
          boxShadow: `0 0 20px ${GREEN}, 0 0 40px ${PURPLE}55`,
          border: `2px solid ${GREEN}`
        }}
      >
        <QRCodeSVG
          data-testid="buzz-passport-qr"
          value={data.qr_payload}
          size={180}
          bgColor="#ffffff"
          fgColor="#000000"
          level="M"
        />
      </div>

      {/* Active Promo Codes */}
      <div className="mb-5">
        <p
          className="text-xs uppercase tracking-widest mb-2 font-bold"
          style={{ color: GREEN, textShadow: `0 0 6px ${GREEN}` }}
        >
          Active Promo Codes
        </p>
        <div className="space-y-2">
          {data.codes.map((c, idx) => (
            <div
              key={c.code + idx}
              data-testid={`buzz-code-${c.code}`}
              onClick={() => copyCode(c.code)}
              className="flex items-center justify-between p-3 rounded cursor-pointer transition hover:scale-[1.01]"
              style={{
                background: 'rgba(157, 0, 255, 0.08)',
                border: `1px solid ${PURPLE}88`,
                boxShadow: `0 0 10px ${PURPLE}44`
              }}
            >
              <div>
                <p className="font-mono font-black" style={{ color: GREEN, letterSpacing: 2, textShadow: `0 0 8px ${GREEN}` }}>
                  {c.code}
                </p>
                <p className="text-xs text-white/80">{c.venue}</p>
              </div>
              <span
                className="px-2 py-1 rounded text-xs font-bold"
                style={{ background: PURPLE, color: '#fff', boxShadow: `0 0 8px ${PURPLE}` }}
              >
                {c.discount}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Sponsor banner slots */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[0, 1].map(i => (
          <div
            key={i}
            data-testid={`buzz-sponsor-slot-${i}`}
            className="rounded overflow-hidden relative flex items-center justify-center"
            style={{
              width: '100%',
              maxWidth: 300,
              height: 80,
              background: 'rgba(0,0,0,0.6)',
              border: `1px solid ${i === 0 ? GREEN : PURPLE}`,
              boxShadow: `0 0 14px ${i === 0 ? GREEN : PURPLE}66`,
              margin: '0 auto'
            }}
          >
            <span
              className="absolute top-1 left-1 text-[9px] font-mono tracking-widest px-2 py-0.5 rounded"
              style={{ background: 'rgba(0,0,0,0.7)', color: i === 0 ? GREEN : PURPLE }}
            >
              SPONSORED
            </span>
            <span className="text-xs text-white/60">Ad Slot {i + 1}</span>
          </div>
        ))}
      </div>
      {/* Full-screen QR modal */}
      {fullQr && (
        <div
          data-testid="fullscreen-qr-modal"
          onClick={() => setFullQr(null)}
          className="fixed inset-0 z-[10000] bg-black/95 flex flex-col items-center justify-center p-6 cursor-pointer"
        >
          <button
            onClick={() => setFullQr(null)}
            className="absolute top-4 right-4 p-2 rounded-full"
            style={{ background: 'rgba(255,255,255,0.1)' }}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="text-center mb-4">
            <p className="text-4xl mb-2">{fullQr.icon}</p>
            <h3 className="font-black text-2xl mb-1" style={{ color: GOLD, textShadow: `0 0 12px ${GOLD}` }}>
              {fullQr.title}
            </h3>
            <p className="text-sm text-white/70">at {fullQr.venue_name}</p>
          </div>
          <div style={{
            background: '#fff', padding: 20, borderRadius: 12,
            boxShadow: `0 0 40px ${GOLD}`,
            border: `3px solid ${GOLD}`
          }}>
            <QRCodeSVG value={fullQr.qr_data} size={320} level="H" />
          </div>
          <p className="mt-6 text-xs uppercase tracking-widest" style={{ color: GOLD }}>
            Show this to your bartender · Tap anywhere to close
          </p>
        </div>
      )}
    </Card>
  );
}
